import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ICbpEntryNumber } from 'src/app/features/model/mawb.model';
import { MawbService } from 'src/app/features/service/mawb.service';
import { SvgIconComponent } from 'src/app/shared/components/svg-icon/svg-icon.component';
import { MaterialModule } from 'src/app/shared/material.module';
import {
  ActionIcons,
  ModalPopupMessages,
  popupButtons,
} from 'src/app/shared/utility/constants';
import { cbpEntryNumbersGridColumns } from '../../mawb.config';

export interface CbpEntryNumbersDialogData {
  mawbId: number;
}

/** Grid row: API row plus a display value for Total Value without the `$`. */
interface CbpEntryNumberRow extends ICbpEntryNumber {
  totalValueDisplay: string;
}

/** PAC-24713 AC3: read-only, searchable, sortable US CBP Entry Numbers grid. */
@Component({
  selector: 'app-cbp-entry-numbers-popup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    SvgIconComponent,
  ],
  templateUrl: './cbp-entry-numbers-popup.component.html',
  styleUrl: './cbp-entry-numbers-popup.component.scss',
})
export class CbpEntryNumbersPopupComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  private readonly dialogRef = inject(
    MatDialogRef<CbpEntryNumbersPopupComponent>
  );
  private readonly dialogData = inject(MAT_DIALOG_DATA);
  private readonly mawbService = inject(MawbService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly messages = ModalPopupMessages;
  public readonly popupButtons = popupButtons;
  public readonly icons = ActionIcons;
  public readonly columns = cbpEntryNumbersGridColumns;
  public readonly displayedColumns: string[] = cbpEntryNumbersGridColumns.map(
    column => column.key
  );
  public readonly searchControl = new FormControl<string>('', {
    nonNullable: true,
  });
  public readonly dataSource = new MatTableDataSource<CbpEntryNumberRow>([]);
  public isLoading = true;
  public activeSort: Sort = { active: '', direction: '' };

  ngOnInit(): void {
    this.dataSource.filterPredicate = (row, term) =>
      row.shipment?.toLowerCase().includes(term) ||
      row.entryNumber?.toLowerCase().includes(term);
    this.dataSource.sortingDataAccessor = (row, key) => {
      if (key === 'totalValue') {
        return this.parseAmount(row.totalValue);
      }
      if (key === 'pieceCount') {
        return Number(row.pieceCount) || 0;
      }
      return (row[key as keyof ICbpEntryNumber] ?? '').toString().toLowerCase();
    };

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.dataSource.filter = value.trim().toLowerCase();
      });

    this.loadEntryNumbers();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.sort.sortChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(sort => (this.activeSort = sort));
  }

  public get hasSearchTerm(): boolean {
    return !!this.searchControl.value;
  }

  public get emptyMessage(): string {
    return this.dataSource.data.length
      ? this.messages.usCbpEntryNumbersNoMatch
      : this.messages.usCbpEntryNumbersNotAvailable;
  }

  public getSortCondition(key: string): string {
    return this.activeSort.active === key ? this.activeSort.direction : '';
  }

  public getCellValue(row: CbpEntryNumberRow, key: string): string | number {
    return key === 'totalValue'
      ? row.totalValueDisplay
      : row[key as keyof ICbpEntryNumber];
  }

  public clearSearch(): void {
    this.searchControl.setValue('');
  }

  public onClose(): void {
    this.dialogRef.close(false);
  }

  private loadEntryNumbers(): void {
    const payload: CbpEntryNumbersDialogData =
      this.dialogData?.code ?? this.dialogData;
    this.mawbService
      .getCbpEntryNumbers(payload.mawbId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          // API order is kept as the default order (no initial sort).
          this.dataSource.data = (response?.data ?? []).map(row => ({
            ...row,
            totalValueDisplay: (row.totalValue ?? '').replace(/\$/g, '').trim(),
          }));
          this.isLoading = false;
        },
        error: () => {
          // Toast already shown by the service errorHandler.
          this.dataSource.data = [];
          this.isLoading = false;
        },
      });
  }

  private parseAmount(value: string): number {
    const parsed = Number((value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
